import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ShieldCheck, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { generateGeneralWhatsAppLink } from '../data/products';
import { HERO_IMAGES } from '../data/images';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  // Preload all hero images on mount for instant switching
  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const goToSlide = useCallback((index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  }, []);

  // Auto-advance every 4 seconds
  useEffect(() => {
    const timer = setInterval(goNext, 4000);
    return () => clearInterval(timer);
  }, [goNext]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.215, 0.61, 0.355, 1.0] }
    }
  };

  const lineVariants = {
    hidden: { opacity: 0, x: -24 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeOut' }
    }
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
    exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0, transition: { duration: 0.4, ease: 'easeIn' } }),
  };

  return (
    <section
      className="hero-section"
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - var(--nav-height))',
        paddingTop: 'calc(var(--nav-height) + 32px)',
        paddingBottom: '60px',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, rgba(224, 0, 135, 0.05) 100%)'
      }}
    >
      {/* Animated background glow */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.5, 0.25] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', top: '10%', right: '2%',
          width: '420px', height: '420px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(224, 0, 135, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: '48px',
            alignItems: 'center'
          }}
        >
          {/* ── LEFT COLUMN: Text & CTAs ── */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible">

            <motion.div variants={itemVariants} className="section-badge" style={{ marginBottom: '22px' }}>
              <Globe size={13} />
              <span>Global Apparel Exporter</span>
            </motion.div>

            {/* Kinetic headline */}
            <motion.h1
              style={{
                fontSize: 'clamp(40px, 5.2vw, 72px)',
                fontFamily: 'var(--font-heading)',
                fontWeight: 900,
                lineHeight: 1.05,
                textTransform: 'uppercase',
                marginBottom: '22px',
                color: 'var(--text-main)',
                letterSpacing: '-0.02em'
              }}
            >
              <motion.span variants={lineVariants} style={{ display: 'block' }}>
                INTERNATIONAL,
              </motion.span>
              <motion.span variants={lineVariants} className="gradient-text" style={{ display: 'block' }}>
                CORPORATE 
              </motion.span>
              <motion.span variants={lineVariants} style={{ display: 'block', color: 'var(--text-main)' }}>
                DENIM EXPORTER
              </motion.span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              style={{
                fontSize: 'clamp(16px, 1.7vw, 19px)', lineHeight: 1.6,
                color: 'var(--text-muted)', marginBottom: '32px',
                maxWidth: '560px', fontWeight: 500
              }}
            >
              Denim collections and export supply for malls, multi-brand outlets (MBOs), retail chains, importers, distributors, and apparel brands.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '28px' }}>
              <motion.a
                whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                href={generateGeneralWhatsAppLink('Export Enquiry')}
                target="_blank" rel="noopener noreferrer"
                className="btn-primary"
                style={{ padding: '15px 28px', fontSize: '13px' }}
              >
                START AN EXPORT ENQUIRY
                <ArrowRight size={16} />
              </motion.a>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link to="/collections" className="btn-secondary" style={{ padding: '15px 24px', fontSize: '13px' }}>
                  EXPLORE COLLECTIONS
                </Link>
              </motion.div>
            </motion.div>

            {/* Quick Category Navigation Shortcuts (American Eagle & Van Heusen Style) */}
            <motion.div variants={itemVariants} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' }}>
              {[
                { label: 'RINSIN BLUE', path: '/collections?category=classic-blue' },
                { label: 'RINSE BLUE', path: '/collections?category=dark-selvage' },
                { label: 'MID BLUE', path: '/collections?category=black-denim' },
                { label: 'DK GREY', path: '/collections' },
                { label: 'DK J BLACK', path: '/collections' },
                { label: 'INK BLUE', path: '/collections' },
                { label: 'DARK INDIGO', path: '/collections' },
                { label: 'RESIN BLUE', path: '/collections' },
                { label: 'BLACK SELVEDGE', path: '/collections?category=dark-selvage' },
                { label: 'BLUE SELVEDGE', path: '/collections?category=dark-selvage' },
                { label: 'DK BLUE', path: '/collections?category=dark-selvage' },
                { label: 'DK BLACK', path: '/collections?category=black-denim' },
                { label: 'LIGHT BLACK', path: '/collections?category=black-denim' },
                { label: 'LIGHT BLUE', path: '/collections?category=classic-blue' },
                { label: 'BLUE BLACK', path: '/collections?category=black-denim' },
                { label: 'VINTAGE BLUE', path: '/collections?category=vintage-washed' },
              ].map((chip, idx) => (
                <Link
                  key={idx}
                  to={chip.path}
                  style={{
                    fontSize: '11px',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    padding: '6px 14px',
                    borderRadius: '99px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    color: 'var(--text-main)',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)'
                  }}
                >
                  {chip.label}
                </Link>
              ))}
            </motion.div>

            {/* Trust badges */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'flex', gap: '24px', flexWrap: 'wrap',
                paddingTop: '24px', borderTop: '1px solid rgba(15, 23, 42, 0.08)'
              }}
            >
              {[
                { icon: <ShieldCheck size={18} />, title: 'Strict QC Standards', sub: 'Multi-Point Checks' },
                { icon: <Globe size={18} />, title: 'Worldwide Export Logistics', sub: 'International Shipping Ready' },
              ].map((badge, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: 'rgba(224, 0, 135, 0.08)', border: '1px solid rgba(224, 0, 135, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-magenta)', flexShrink: 0 }}>
                    {badge.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-main)' }}>{badge.title}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-subtle)' }}>{badge.sub}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* ── RIGHT COLUMN: Auto-Sliding Product Carousel ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.85, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'relative' }}
          >
            {/* Main Slider Frame */}
            <div
              style={{
                position: 'relative',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 28px 56px rgba(15, 23, 42, 0.14), 0 0 36px rgba(224, 0, 135, 0.12)',
                border: '1px solid rgba(15, 23, 42, 0.07)',
                aspectRatio: '4/5',
                background: '#f1f5f9'
              }}
            >
              <AnimatePresence custom={direction} initial={false}>
                <motion.img
                  key={currentSlide}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  src={HERO_IMAGES[currentSlide]}
                  alt={`RAYYAN DENIM Collection ${currentSlide + 1}`}
                  fetchpriority={currentSlide === 0 ? 'high' : 'auto'}
                  loading={currentSlide === 0 ? 'eager' : 'lazy'}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center center',
                    display: 'block', willChange: 'transform'
                  }}
                />
              </AnimatePresence>

              {/* Dark gradient overlay at bottom */}
              <div
                style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '24px 20px 16px',
                  background: 'linear-gradient(to top, rgba(15,23,42,0.88) 0%, transparent 100%)',
                  zIndex: 2
                }}
              >
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: '17px', color: '#fff', letterSpacing: '0.02em' }}>
                  <span className="brand-name">RAYYAN DENIM</span> — EXPORT COLLECTION
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '3px' }}>
                  Global Export Sourcing • Slide {currentSlide + 1} of {HERO_IMAGES.length}
                </div>
              </div>

              {/* Left / Right arrow controls */}
              <button
                onClick={goPrev}
                aria-label="Previous image"
                style={{
                  position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                  zIndex: 5, width: '36px', height: '36px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.92)', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-main)', boxShadow: '0 2px 10px rgba(15,23,42,0.15)'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={goNext}
                aria-label="Next image"
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  zIndex: 5, width: '36px', height: '36px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.92)', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-main)', boxShadow: '0 2px 10px rgba(15,23,42,0.15)'
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Dot Indicators */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '14px' }}>
              {HERO_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  aria-label={`Slide ${idx + 1}`}
                  style={{
                    width: idx === currentSlide ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '99px',
                    background: idx === currentSlide
                      ? 'linear-gradient(90deg, var(--accent-purple), var(--accent-magenta))'
                      : 'rgba(15, 23, 42, 0.18)',
                    border: 'none', cursor: 'pointer', padding: 0,
                    transition: 'all 0.35s ease'
                  }}
                />
              ))}
            </div>

            {/* Floating Brand Badge */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.9, type: 'spring', stiffness: 220 }}
              style={{
                position: 'absolute', top: '-18px', right: '-12px',
                background: '#ffffff', border: '1px solid rgba(224, 0, 135, 0.25)',
                borderRadius: '12px', padding: '10px 16px',
                boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
              }}
            >
              <img src="/Rayyandenimlogo.jpeg" alt="Rayyan Denim Official Logo" style={{ height: '38px', objectFit: 'contain' }} />
            </motion.div>
          </motion.div>

        </div>
      </div>

      {/* Responsive mobile grid fix */}
      <style>{`
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
